import { AuthService } from '@/http-services/auth.service';
import { useIonRouter } from '@ionic/vue';
import { defineStore } from 'pinia';
import { ref, watch, watchEffect } from 'vue';
import { useDeviceInfo } from './device-info.store';
import { useAppInfo } from './app-info.store';
import { usePreferenceStorage } from '@/composables/use-preference-storage';
import { UserModel } from '@ncss/models/dist/v3';
import { HttpService } from '@/http-services/http.service';
import { LoginResponse } from 'common';
import { setUser as setSentryUser } from '@sentry/vue';
import { useBiometrics } from './biometrics.store';

export type AuthState = 'INITIALIZING' | 'AUTHENTICATING' | 'UNAUTHENTICATED' | 'AUTHENTICATED';
export const useAuth = defineStore('AuthStore', () => {
  const auth = new AuthService();
  const router = useIonRouter();
  const deviceInfo = useDeviceInfo();
  const appInfo = useAppInfo();
  const biometrics = useBiometrics();

  const state = ref<AuthState>('INITIALIZING');
  const stateChangeCallbacks: Array<(state: AuthState) => void> = [];
  watch(state, (val) => {
    let cb = stateChangeCallbacks.pop();
    while (cb) {
      cb(val);
      cb = stateChangeCallbacks.pop();
    }
  });
  const { state: token, waitForInit: waitForTokenInit } = usePreferenceStorage<string>('ncss-mobile-token', {
    default: '',
  });
  const { state: user, waitForInit: waitForUserInit } = usePreferenceStorage<UserModel>('ncss-mobile-user', {
    json: true,
  });

  watch(token, (val) => (HttpService.AccessToken = val));
  watch([token, user], () => {
    if (state.value !== 'INITIALIZING') {
      if (token.value && user.value) {
        state.value = 'AUTHENTICATED';
      } else {
        state.value = 'UNAUTHENTICATED';
      }
    }
  });

  function setUser(u: UserModel | undefined) {
    if (u) {
      setSentryUser({ email: u._id, id: u._id, username: u.username || `${u.firstname} ${u.lastname}` });
    } else {
      setSentryUser(null);
    }
    user.value = u;
  }

  async function biometricLogin(): Promise<LoginResponse> {
    const credentials = await biometrics.getCredentials();
    if (!credentials) {
      return {
        success: false,
        message: `Biometric login unsuccessful`,
      };
    }
    const response = await login(credentials.username, credentials.password);
    return response;
  }

  async function login(usernameOrEmail: string, password: string): Promise<LoginResponse> {
    state.value = 'AUTHENTICATING';
    const response = await auth.login(usernameOrEmail, password, {
      deviceId: deviceInfo.deviceId,
      deviceInfo: deviceInfo.deviceInfo,
      appVersion: appInfo.appVersion,
    });
    if (response.success) {
      await biometrics.setCredentials(usernameOrEmail, password);
      token.value = response.token;
      setUser(response.user);
    } else {
      token.value = undefined;
      setUser(undefined);
    }
    return response;
  }

  function logout() {
    token.value = undefined;
    setUser(undefined);
    return router.replace('/login');
  }

  async function refresh(): Promise<LoginResponse> {
    state.value = 'AUTHENTICATING';
    if (user.value && token.value && deviceInfo.deviceId) {
      const response = await auth.refreshToken(user.value._id, deviceInfo.deviceId, token.value, {
        appVersion: appInfo.appVersion,
        deviceInfo: deviceInfo.deviceInfo,
      });
      if (response.success) {
        token.value = response.token;
        setUser(response.user);
      } else {
        token.value = undefined;
        setUser(undefined);
      }
      return response;
    }
    console.log('Cannot refresh token without user, token, and deviceId');
    return { success: false };
  }

  function waitForStateChange(): Promise<AuthState> {
    return new Promise<AuthState>((resolve) => {
      stateChangeCallbacks.push(resolve);
    });
  }

  async function init() {
    state.value = 'INITIALIZING';
    await waitForTokenInit();
    await waitForUserInit();
    state.value = user.value && token.value ? 'AUTHENTICATED' : 'UNAUTHENTICATED';
  }

  watchEffect(() => console.log('STATE', state.value));

  watch(state, (val, old) => {
    if (val === 'AUTHENTICATED' && old !== 'AUTHENTICATED') {
      appInfo.setUserChannel();
    }
  });

  init();
  return {
    login,
    refresh,
    user,
    token,
    state,
    waitForStateChange,
    logout,
    biometricLogin,
    setUser,
  };
});
